import type {
    Rectangle,
    Point,
    DisplayObject,
    Graphics,
    Container,
    Sprite,
    Text,
    BitmapText,
    TilingSprite,
    AnimatedSprite,
    Mesh,
    NineSlicePlane,
    SimpleMesh,
    SimplePlane,
    SimpleRope,
} from 'pixi.js';
import type {RRectangle} from './RRectangle';
import type {RPoint} from './RPoint';
import type {Size2D} from '@gongxigames/framework/core/types';
import type {RichText} from '@gongxigames/framework/core/fragments/display/richText/RichText';

export const RESPONSIVE_CHANGE_EVENT = 'responsiveChange' as const;

export type RUnitType = 'px' | '%' ;
export type RUnit = `${number}${RUnitType}` | '0';

export type ResponsiveRectangleOptions = {
    x?: RUnit;
    y?: RUnit;
    width?: RUnit;
    height?: RUnit;
};

export type ResponsivePointOptions = {
    x?: RUnit;
    y?: RUnit;
};

export type Padding = [RUnit] | [RUnit, RUnit] | [RUnit, RUnit, RUnit, RUnit];

export type RDisplayObject = DisplayObject & {
    children: RDisplayObject[];
    responsive?: ResponsiveConf | ResponsiveConfNormalized;
};

export type RContainerObj = Omit<Container, 'children'> & {
    children: RContainerObj[];
    responsive?: ResponsiveConf | ResponsiveConfNormalized;
};

export type ResponsiveConf = {
    skipChildren?: boolean;
    scale?: number;
    box?: ResponsiveRectangleOptions;
    translate?: ResponsivePointOptions;
    padding?: Padding;
    updateWidth?: boolean;
    updateHeight?: boolean;
};

export type ResponsiveConfNormalized = {
    skipChildren?: boolean;
    scale?: number;
    box?: RRectangle;
    translate?: RPoint;
    padding?: Padding;
    updateWidth?: boolean;
    updateHeight?: boolean;
    _responsiveCache: ResponsiveCache;
};

export type ResponsiveCache = {
    previous_boundingBox: Rectangle;
    boundingBox: Rectangle;
    paddingBox: Rectangle;
    contextForChildren: Rectangle;
    contextScaled: Rectangle;
    size: Size2D;
    point: Point;
};

export type DebugBoxConf = {
    container: DisplayObject & Responsive;
    color?: number;
    alpha?: number;
    outlineColor?: number;
    outlineWith?: number;
    outlineAlpha?: number;
    borderRadius?: number;
};

export interface Responsive {
    /**
     * Configure dynamic container sizing according to parent **responsive** container
     */
    responsive?: ResponsiveConf;
};

export type RContainer = Container & Responsive;
export type RGraphics = Graphics & Responsive;
export type RSprite = Sprite & Responsive;
export type RText = Text & Responsive;
export type RBitmapText = BitmapText & Responsive;
export type RTilingSprite = TilingSprite & Responsive;
export type RAnimatedSprite = AnimatedSprite & Responsive;
export type RMesh = Mesh & Responsive;
export type RNineSlicePlane = NineSlicePlane & Responsive;
export type RSimpleMesh = SimpleMesh & Responsive;
export type RSimplePlane = SimplePlane & Responsive;
export type RSimpleRope = SimpleRope & Responsive;
export type RRichText = RichText & Responsive;